// client/src/pages/EditProfile.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import colleges from "../data/colleges";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function EditProfile() {
  const { user, refreshUser, setUser } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [college, setCollege] = useState("");
  const [year, setYear] = useState("");
  const [password, setPassword] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [saving, setSaving] = useState(false);

  // small search for colleges
  const [collegeSearch, setCollegeSearch] = useState("");
  const [filteredColleges, setFilteredColleges] = useState(colleges);

  const inputRef = useRef(null);

  useEffect(() => {
    if (!user) return navigate("/login");
    setName(user.name || "");
    setCollege(user.college || "");
    setYear(user.year || "");
    setPhotoPreview(user.profilePhoto || "");
  }, [user, navigate]);

  useEffect(() => {
    const term = (collegeSearch || "").trim().toLowerCase();
    if (!term) return setFilteredColleges(colleges.slice(0, 50));
    setFilteredColleges(
      colleges.filter((c) => c.name.toLowerCase().includes(term)).slice(0, 50)
    );
  }, [collegeSearch]);

  const handleFile = (f) => {
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const uploadImage = async () => {
    if (!photoFile) return null;
    const fd = new FormData();
    fd.append("image", photoFile);
    const token = localStorage.getItem("cp_token");
    const res = await fetch(`${API}/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || "Upload failed");
    }
    const data = await res.json();
    // support multiple shapes
    return data.url || data.imageUrl || data.path || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("cp_token");
      let imageUrl = photoPreview;
      if (photoFile) {
        const uploaded = await uploadImage();
        if (uploaded) imageUrl = uploaded;
      }

      const body = { name, college, year };
      if (password) body.password = password;
      if (imageUrl) body.profilePhoto = imageUrl;

      const res = await fetch(`${API}/auth/update`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Update failed");

      // server returns { message, user }
      const updatedUser = d.user || (await refreshUser());
      if (updatedUser) {
        // update context immediately
        setUser(updatedUser);
      } else {
        await refreshUser();
      }

      alert("Profile updated");
      navigate("/profile");
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const yearOptions = [
    "1st Year",
    "2nd Year",
    "3rd Year",
    "4th Year",
    "Other",
  ];

  return (
    <div className="min-h-screen p-6 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 p-6 rounded-xl shadow">
        <h2 className="text-2xl font-bold mb-4 text-slate-900 dark:text-white">Edit Profile</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required className="w-full mt-1 p-2 border rounded dark:bg-slate-700 dark:text-white" />
          </div>

          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300">College</label>

            <div className="mt-1">
              <input
                value={collegeSearch}
                onChange={(e) => setCollegeSearch(e.target.value)}
                placeholder="Search colleges..."
                className="w-full p-2 border rounded dark:bg-slate-700 dark:text-white mb-2"
              />

              <div className="relative">
                <select
                  value={college}
                  onChange={(e) => setCollege(e.target.value)}
                  className="w-full p-2 border rounded dark:bg-slate-700 dark:text-white"
                >
                  <option value="">-- Select college --</option>
                  {filteredColleges.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name} {c.city ? `— ${c.city}` : ""}
                    </option>
                  ))}
                  <option value="Other">Other (enter manually)</option>
                </select>
              </div>

              {college === "Other" && (
                <input
                  className="w-full mt-2 p-2 border rounded dark:bg-slate-700 dark:text-white"
                  placeholder="Type your college name"
                  value={college !== "Other" ? college : ""}
                  onChange={(e) => setCollege(e.target.value)}
                />
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300">Year</label>
            <select value={year} onChange={(e) => setYear(e.target.value)} className="w-full mt-1 p-2 border rounded dark:bg-slate-700 dark:text-white">
              <option value="">-- Select year --</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300">Change Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave empty to keep existing" className="w-full mt-1 p-2 border rounded dark:bg-slate-700 dark:text-white" />
          </div>

          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300 mb-2">Profile Photo</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-100">
                {photoPreview ? <img src={photoPreview} alt="preview" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400">No</div>}
              </div>

              <div>
                <input ref={inputRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} />
                <div className="text-xs text-slate-500 mt-1">Max 5MB recommended</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded">{saving ? "Saving…" : "Save Changes"}</button>
            <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border rounded">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
